import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { dashboardFinanceiroService } from "../../services/apiService";
import { useQuery } from "../../hooks/useQuery";
import { useNotification } from "../../hooks/useNotification";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  TextInput,
  cardClass,
  tableCompactClass,
  tableEmptyStateClass,
  tableWrapperClass,
} from "../../components/ui";
import { formatCurrencyMoeda } from "../../utils/FormatarValores";
import {
  getCompetenciaAtual,
  competenciaParaInputMes,
  inputMesParaCompetencia,
  formatarCompetenciaMesAno,
} from "../../utils/FormatarCompetencia";

const mesAbreviado = (mesStr) => {
  const mesAno = formatarCompetenciaMesAno(mesStr);
  return mesAno === "-" ? mesStr || "" : mesAno;
};

const formatarPercent = (valor) => {
  const numero = Number(valor || 0);
  return `${numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const corVariacao = (valor) =>
  Number(valor || 0) < 0 ? "text-red-500" : "text-green-600";

const CardResumo = ({ titulo, valor, destaque, tituloClassName }) => {
  return (
    <div className={`${cardClass} p-4`}>
      <p className={`text-sm ${tituloClassName || "text-gray-500"}`}>{titulo}</p>
      <h2 className={`text-xl font-bold ${destaque || "text-gray-900"}`}>
        {valor}
      </h2>
    </div>
  );
};

const EntradaCaixa = () => {
  const { showError } = useNotification();
  const [competencia, setCompetencia] = useState(getCompetenciaAtual());

  const { data, isLoading, error, refetch } = useQuery(
    ["dashboard-financeiro-crescimento-caixa", competencia],
    () => dashboardFinanceiroService.crescimentoCaixaPorCompetencia(competencia),
    {
      enabled: Boolean(competencia),
      onError: (err) => {
        showError(err.message || "Erro ao buscar entrada de caixa");
      },
    }
  );

  const tiposTabela = useMemo(() => {
    const meses = data?.meses || [];
    const tiposMap = new Map();
    meses.forEach((mes) => {
      (mes.caixaMovimentoTipoCaixa || []).forEach((item) => {
        const tipoId = item?.tipoCaixa?.id || item?.tipoCaixa?.nome;
        const tipoNome = item?.tipoCaixa?.nome || "Nao informado";
        if (!tiposMap.has(tipoId)) {
          tiposMap.set(tipoId, { id: tipoId, nome: tipoNome });
        }
      });
    });
    return Array.from(tiposMap.values());
  }, [data]);

  const mesesOrdenados = useMemo(
    () => [...(data?.meses || [])].reverse(),
    [data]
  );

  const trimestres = data?.trimestres || [];

  const trimestreAtual = trimestres[trimestres.length - 1];
  const trimestreAnterior = trimestres[trimestres.length - 2];

  const crescTrimestralValor =
    trimestreAtual && trimestreAnterior
      ? trimestreAtual.total - trimestreAnterior.total
      : null;
  const crescTrimestralPerc =
    trimestreAtual && trimestreAnterior && trimestreAnterior.total > 0
      ? (crescTrimestralValor / trimestreAnterior.total) * 100
      : null;
  const houveReducao = crescTrimestralPerc !== null && crescTrimestralPerc < 0;

  const handleCompetenciaChange = (e) => {
    const value = inputMesParaCompetencia(e.target.value);
    if (!value) {
      return;
    }
    setCompetencia(value);
  };

  return (
    <PageContainer className="p-6 space-y-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold">Entrada de Caixa</h1>

        <div className={`${cardClass} p-4`}>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <FormField
              label="Competência"
              htmlFor="entrada-caixa-competencia"
              className="min-w-[220px]"
            >
              <TextInput
                id="entrada-caixa-competencia"
                type="month"
                value={competenciaParaInputMes(competencia)}
                onChange={handleCompetenciaChange}
              />
            </FormField>
            <Button onClick={refetch} icon={RefreshCw}>
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {isLoading && <p className="text-gray-500">Carregando dados...</p>}
      {error && <p className="text-red-500">{error.message}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CardResumo
          titulo="Total Trimestre Atual"
          valor={formatCurrencyMoeda(trimestreAtual?.total || 0)}
        />
        <CardResumo
          titulo={houveReducao ? "Reducao Trimestral" : "Crescimento Trimestral"}
          valor={
            crescTrimestralValor !== null
              ? `${formatCurrencyMoeda(Math.abs(crescTrimestralValor))} (${formatarPercent(
                  Math.abs(crescTrimestralPerc || 0)
                )})`
              : "-"
          }
          destaque={houveReducao ? "text-red-500" : "text-green-600"}
          tituloClassName={houveReducao ? "text-red-500" : "text-gray-500"}
        />
      </div>

      <div className={`${cardClass} overflow-hidden`}>
        <div className={tableWrapperClass}>
        <table className={`min-w-full ${tableCompactClass}`}>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="p-3 text-left">Mês</th>
              {tiposTabela.map((tipo) => (
                <th key={tipo.id} className="p-3 text-center">Valor {tipo.nome}</th>
              ))}
              <th className="p-3 text-center">Total</th>
              <th className="p-3 text-center">Cresc</th>
              {tiposTabela.map((tipo) => (
                <th key={`perc-${tipo.id}`} className="p-3 text-center">% {tipo.nome}</th>
              ))}
              <th className="p-3 text-center">% Total</th>
            </tr>
          </thead>
          <tbody>
            {mesesOrdenados.length === 0 && (
              <tr>
                <td colSpan={tiposTabela.length * 2 + 4} className={tableEmptyStateClass}>
                  Nenhum dado encontrado para a competência selecionada.
                </td>
              </tr>
            )}
            {mesesOrdenados.map((mes) => {
              const semVariacao = mes.percTotal === null;
              const tipomapa = new Map(
                (mes.caixaMovimentoTipoCaixa || []).map((item) => [
                  item?.tipoCaixa?.id || item?.tipoCaixa?.nome,
                  item,
                ])
              );
              return (
                <tr key={mes.mes} className="border-t hover:bg-gray-50 transition">
                  <td className="p-3 font-medium">{mesAbreviado(mes.mes)}</td>
                  {tiposTabela.map((tipo) => (
                    <td key={tipo.id} className="p-3 text-center">
                      {formatCurrencyMoeda(tipomapa.get(tipo.id)?.valorMovimento || 0)}
                    </td>
                  ))}
                  <td className="p-3 text-center font-bold">
                    {formatCurrencyMoeda(mes.total)}
                  </td>
                  <td className={`p-3 text-center font-semibold ${
                    semVariacao ? "text-gray-400" : corVariacao(mes.crescimento)
                  }`}>
                    {semVariacao ? "-" : formatCurrencyMoeda(Math.abs(mes.crescimento))}
                  </td>
                  {tiposTabela.map((tipo) => {
                    const item = tipomapa.get(tipo.id);
                    const perc = item?.variacaoPercentual;
                    return (
                      <td key={`perc-${tipo.id}`} className={`p-3 text-center ${
                        semVariacao ? "text-gray-400" : corVariacao(perc)
                      }`}>
                        {semVariacao ? "-" : formatarPercent(Math.abs(perc))}
                      </td>
                    );
                  })}
                  <td className={`p-3 text-center ${
                    semVariacao ? "text-gray-400" : corVariacao(mes.percTotal)
                  }`}>
                    {semVariacao ? "-" : formatarPercent(Math.abs(mes.percTotal))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {trimestres.length > 0 && (
        <div className={`${cardClass} overflow-hidden`}>
          <div className={tableWrapperClass}>
          <table className={`min-w-full ${tableCompactClass}`}>
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3 text-center">Trimestre</th>
                <th className="p-3 text-center">Total</th>
                <th className="p-3 text-center">Variação</th>
                <th className="p-3 text-center">Diferença</th>
              </tr>
            </thead>
            <tbody>
              {trimestres.map((trim) => (
                <tr key={trim.nome} className="border-t hover:bg-gray-50 transition">
                  <td className="p-3 text-center font-semibold">{trim.nome}</td>
                  <td className="p-3 text-center font-semibold">
                    {formatCurrencyMoeda(trim.total)}
                  </td>
                  <td className={`p-3 text-center font-semibold ${
                    trim.variacao === null ? "text-gray-400" : corVariacao(trim.variacao)
                  }`}>
                    {trim.variacao === null ? "-" : formatarPercent(Math.abs(trim.variacao))}
                  </td>
                  <td className={`p-3 text-center font-semibold ${
                    trim.diferenca === null ? "text-gray-400" : corVariacao(trim.diferenca)
                  }`}>
                    {trim.diferenca === null
                      ? "-"
                      : formatCurrencyMoeda(Math.abs(trim.diferenca))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default EntradaCaixa;
